import { Deployment } from 'types/deployment';
import { Camera } from 'types/camera';
import { Stream } from 'types/stream';

import { useCameras } from 'cameras/hooks/useCameras';
import { useStreams } from 'streams/hooks/useStreams';

export type VideoSource = Camera | Stream;

const EMPTY_CAMERAS: Camera[] = [];
const EMPTY_STREAMS: Stream[] = [];

export type UseDeploymentsVideoSourcesOptions = {
  keepPreviousData?: boolean;
};

export function useDeploymentsVideoSources(
  deployments: Deployment[],
  options?: UseDeploymentsVideoSourcesOptions
) {
  const { cameraIds, streamIds } = deployments.reduce(
    (ids, { configuration }) => {
      for (const { source_id, source_type } of Object.values(configuration)) {
        if (!source_id || !source_type) {
          continue;
        }

        switch (source_type) {
          case 'camera':
            ids.cameraIds.push(source_id);
            break;
          case 'stream':
            ids.streamIds.push(source_id);
            break;
        }
      }

      return ids;
    },
    { cameraIds: [] as string[], streamIds: [] as string[] }
  );

  const hasStreams = streamIds.length > 0;
  const hasCameras = cameraIds.length > 0;

  const { data: streams, isInitialLoading: isLoadingStreams } = useStreams({
    params: {
      stream_ids: streamIds,
      limit: streamIds.length,
    },
    ...options,
    enabled: hasStreams,
    keepPreviousData: true,
  });

  const { data: cameras, isInitialLoading: isLoadingCameras } = useCameras({
    params: {
      camera_ids: cameraIds,
      limit: cameraIds.length,
    },
    ...options,
    enabled: hasCameras,
  });

  return {
    cameras: hasCameras && cameras ? cameras.data : EMPTY_CAMERAS,
    streams: hasStreams && streams ? streams.data : EMPTY_STREAMS,
    isLoading: isLoadingCameras || isLoadingStreams,
  };
}
